import type { BasicCustomer } from '~/api/v2';
import storage from '~/utils/storage';

export const RECENT_LIMIT = 3;

export type RecentEntry = string | BasicCustomer;

function storageKey(userId: number): string {
    return `quick_nav_recent_${userId}`;
}

function isRecentEntry(entry: RecentEntry | null): entry is RecentEntry {
    return (
        typeof entry === 'string' ||
        (typeof entry?.id === 'number' && typeof entry.name === 'string')
    );
}

function storeRecentEntries(userId: number, entries: RecentEntry[]): RecentEntry[] {
    const next = entries.slice(0, RECENT_LIMIT);
    storage.setItem(storageKey(userId), JSON.stringify(next));
    return next;
}

export function getRecentEntries(userId: number): RecentEntry[] {
    const raw = storage.getItem(storageKey(userId));
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw) as (RecentEntry | null)[];
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isRecentEntry).slice(0, RECENT_LIMIT);
    } catch {
        return [];
    }
}

export function pushRecentPath(
    userId: number,
    path: string,
    allowedPaths: Set<string>,
): RecentEntry[] {
    if (!allowedPaths.has(path)) {
        return getRecentEntries(userId);
    }

    return storeRecentEntries(userId, [
        path,
        ...getRecentEntries(userId).filter((entry) => entry !== path),
    ]);
}

export function pushRecentCustomer(userId: number, customer: BasicCustomer): RecentEntry[] {
    return storeRecentEntries(userId, [
        customer,
        ...getRecentEntries(userId).filter(
            (entry) => typeof entry === 'string' || entry.id !== customer.id,
        ),
    ]);
}
